import React from "react";

const steps = [
  {
    num: '01',
    tag: 'classification',
    title: 'Binary Classification Task — Explicit Safety Signals',
    body: (
      <>
        Inspired by BERT, we add a dedicated{' '}
        <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">[CLS]</code>{' '}
        token to each input so the model can classify whether the input and
        its current generation trajectory are benign or malicious. The
        classification task runs alongside the language modeling objective but
        is <strong>isolated</strong> via a tailored attention mechanism and
        controlled loss weighting &mdash; generative performance is preserved
        while explicit safety signals are extracted.
      </>
    ),
    img: '/figures/classification_task.jpg',
    caption: 'Figure 1. Binary classification task with the [CLS] token.',
  },
  {
    num: '02',
    tag: 'attention',
    title: 'Strategic Attention Design — Implicit Leverage',
    body: (
      <>
        We let the <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">[CLS]</code> token{' '}
        <strong>dynamically re-attend</strong> to query and response tokens
        during generation, continuously assessing safety without breaking
        causal attention. Three adaptive rules handle benign, malicious, or
        mid-generation transition cases &mdash; enabling nuanced safety
        tracking across complex prompt structures.
      </>
    ),
    img: '/figures/strategic_attention.jpg',
    caption: 'Figure 2. Strategic attention re-attends [CLS] to the rolling response.',
  },
  {
    num: '03',
    tag: 'decoding',
    title: 'Strategic Decoding Strategy — Explicit Leverage',
    body: (
      <>
        To strengthen controllability, we explicitly use the{' '}
        <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">[CLS]</code> prediction
        to modify decoding. When malicious intent is detected (either
        initially or mid-generation), our strategy inserts a{' '}
        <strong>refusal prefix with chain-of-thought justification</strong>{' '}
        &mdash; balancing false-positive risk with interpretability while
        ensuring prompt safety-aware shifts in response generation.
      </>
    ),
    img: '/figures/strategic_decoding.jpg',
    caption: 'Figure 3. Strategic decoding inserts a refusal prefix on malicious detection.',
  },
];

export default function Methodology() {
  return (
    <section className="py-20 bg-white border-t border-ink-100">
      <div className="max-w-5xl mx-auto px-6">
        <p className="section-eyebrow mb-3">03 &middot; Approach</p>
        <h2 className="section-title">Methodology</h2>

        <p className="mt-6 text-center text-ink-500 max-w-2xl mx-auto">
          Three components, working together to make safety alignment{' '}
          <em>not</em> superficial.
        </p>

        <div className="mt-12 space-y-12">
          {steps.map((s) => (
            <div
              key={s.num}
              className="card p-6 md:p-8 hover:shadow-card transition-shadow"
            >
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-grad-brand-accent text-white font-bold text-sm shadow-sm">
                  {s.num}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-600 bg-brand-50 rounded-full px-2 py-0.5">
                  {s.tag}
                </span>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-ink-900 font-serif">
                {s.title}
              </h3>
              <p className="mt-3 text-ink-600 leading-relaxed text-[0.98rem]">
                {s.body}
              </p>
              <figure className="mt-6">
                <div className="rounded-xl border border-ink-100 overflow-hidden bg-white">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-auto"
                  />
                </div>
                <figcaption className="figure-caption">{s.caption}</figcaption>
              </figure>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
